import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, LoginResponse, AdminPermissions } from '../models/admin.models';

const TOKEN_KEY = 'sparex_admin_token';
const NAME_KEY = 'sparex_admin_name';
const USER_KEY = 'sparex_admin_user';
const ROLE_KEY = 'sparex_admin_role';
const PERMS_KEY = 'sparex_admin_perms';

const ALL_PERMS: AdminPermissions = {
  canViewDashboard: true,
  canViewModeration: true,
  canViewSellers: true,
  canViewUsers: true,
  canViewVehicles: true,
  canViewOrders: true,
  canViewCms: true,
  canViewSettings: true,
  canViewScraper: true,
  canManageTeam: true
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly fullName = signal<string | null>(localStorage.getItem(NAME_KEY));
  readonly userId = signal<string | null>(localStorage.getItem(USER_KEY));
  readonly adminRole = signal<number>(Number(localStorage.getItem(ROLE_KEY) || '0'));
  readonly permissions = signal<AdminPermissions>(loadPerms());

  readonly isSuper = computed(() => this.adminRole() === 1);
  readonly isAdminOrAbove = computed(() => this.adminRole() === 1 || this.adminRole() === 2);

  constructor(private http: HttpClient, private router: Router) {}

  login(email: string, password: string): Observable<ApiResponse<LoginResponse>> {
    return this.http
      .post<ApiResponse<LoginResponse>>(`${environment.apiBaseUrl}/auth/login`, { email, password })
      .pipe(
        tap((res) => {
          if (res.success && res.data) {
            localStorage.setItem(TOKEN_KEY, res.data.token);
            localStorage.setItem(NAME_KEY, res.data.fullName);
            localStorage.setItem(USER_KEY, res.data.userId);
            localStorage.setItem(ROLE_KEY, String(res.data.adminRole));
            localStorage.setItem(PERMS_KEY, JSON.stringify(res.data.permissions));
            this.fullName.set(res.data.fullName);
            this.userId.set(res.data.userId);
            this.adminRole.set(res.data.adminRole);
            this.permissions.set(res.data.permissions ?? ALL_PERMS);
          }
        })
      );
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(NAME_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(PERMS_KEY);
    this.fullName.set(null);
    this.userId.set(null);
    this.adminRole.set(0);
    this.permissions.set(ALL_PERMS);
    this.router.navigateByUrl('/login');
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  can(perm: keyof AdminPermissions): boolean {
    if (this.isAdminOrAbove()) return true;
    return !!this.permissions()[perm];
  }
}

function loadPerms(): AdminPermissions {
  try {
    const raw = localStorage.getItem(PERMS_KEY);
    return raw ? { ...ALL_PERMS, ...JSON.parse(raw) } : ALL_PERMS;
  } catch {
    return ALL_PERMS;
  }
}
